import { Box, Text } from '@wix/design-system';
import { Experiments, PurchaseMode } from 'enums';
import { RootStore, withInjectedRootStore } from 'providers';
import { usePageModeContext } from 'providers/PageMode/PageMode.provider';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FeatureIcon } from './FeatureIcon';
import s from './Title.scss';

interface ContentItem {
  key: string;
  iconName?: 'check' | 'ai';
}

const allPlansIncludeContent: ContentItem[] = [
  { key: 'premium-purchase-plan.header.fallback_feature.custom_domain' },
  { key: 'premium-purchase-plan.header.fallback_feature.remove_branding' },
  { key: 'premium-purchase-plan.header.fallback_feature.customer_care' },
];

const newLowTierPlanContent: ContentItem[] = [
  {
    key: 'premium-plan-purchase.header.all_premium_plans_include.reliable_web_hosting',
  },
  {
    key: 'premium-plan-purchase.header.all_premium_plans_include.connect_domain',
  },
  {
    key: 'premium-plan-purchase.header.all_premium_plans_include.customer_care',
  },
];

const freeCouponsContent: ContentItem[] = [
  { key: 'premium-purchase-plan.header.all_plans_include.custom_domain_be_em' },
  {
    key: 'premium-purchase-plan.header.all_plans_include.email_marketing_plan',
  },
  {
    key: 'premium-purchase-plan.header.all_plans_include.social_media_marketing',
  },
];

const aiFeaturesContent: ContentItem[] = [
  ...allPlansIncludeContent,
  {
    key: 'premium-purchase-plan.header.all_plans_include.ai_credits',
    iconName: 'ai',
  },
];

interface AllPlansIncludeProps {
  rootStore: RootStore;
}

export const AllPlansIncludeComp: React.FC<AllPlansIncludeProps> = ({
  rootStore,
}) => {
  const { t } = useTranslation();
  const { purchaseMode, isChangePlan } = usePageModeContext();
  const {
    appStore: { experiments },
  } = rootStore;
  let content = allPlansIncludeContent;
  if (experiments[Experiments.NewLowTierPlan] === 'true') {
    content = newLowTierPlanContent;
  } else if (
    experiments[Experiments.EnableImmediateCouponsDeliverySpec] === 'true'
  ) {
    content = freeCouponsContent;
  } else if (experiments[Experiments.EnableAiFeatures] === 'true') {
    content = aiFeaturesContent;
  }

  let titleKey = 'premium-purchase-plan.header.all_premium_plans_include.title';
  if (experiments[Experiments.EnableImmediateCouponsDeliverySpec] === 'true') {
    titleKey =
      'premium-purchase-plan.header.all_plans_include.business_email_email_marketing_title';
  }

  const isReplaceChangePlanPurchaseModeEnabled =
    experiments[Experiments.ReplaceChangePlanPurchaseMode] === 'true';
  const isInChangePlan = isReplaceChangePlanPurchaseModeEnabled
    ? isChangePlan
    : purchaseMode === PurchaseMode.CHANGE_PLAN;
  const isUnifiedChangePlanPhase1Enabled =
    experiments[Experiments.UnifiedChangePlanPhase1] === 'true';

  const getIconMode = () => {
    if (purchaseMode === PurchaseMode.DEFAULT) {
      return 'default';
    }
    return isInChangePlan && !isUnifiedChangePlanPhase1Enabled
      ? 'default'
      : 'sale';
  };

  const iconMode = getIconMode();

  return (
    <Box gap='SP4'>
      <Text className={s.semiBold}>
        <Trans
          i18nKey={titleKey}
          components={{
            b: <b />,
          }}
        />
      </Text>
      {content.map((contentItem) => (
        <Box verticalAlign='middle' gap='SP1' key={contentItem.key}>
          <FeatureIcon
            iconName={contentItem.iconName}
            mode={iconMode}
            dataHook='feature-icon'
          />
          <Text className={s.semiBold}>{t(contentItem.key)}</Text>
        </Box>
      ))}
    </Box>
  );
};

export const AllPlansInclude = withInjectedRootStore(AllPlansIncludeComp);
